// @ts-nocheck
/* eslint-disable no-restricted-syntax,guard-for-in,no-param-reassign,no-lonely-if */
/**
 * @created 11.05.2017
 * @description Contains helper functions for general purposes needed in the Application.
 */

/* eslint-disable no-undef */
/* eslint-disable no-nested-ternary */
import { has, isString, isObject, isNil, isArray } from "lodash";
import { getName, registerLocale } from "i18n-iso-countries";

// Import all images.
// eager loading of images is needed for now for better compatibility.
// See https://vitejs.dev/guide/features.html#glob-import
const imageModules = import.meta.globEager("../assets/img/**/*.png");
const localeModules = import.meta.globEager(
  "../config/langs/{bg,cs,da,de,el,es,et,fr,hr,hu,it,lt,lv,nl,nb,pl,pt,ro,sk,sl,fi,sv}.json"
);
const RELATIVE_PATH_TO_IMAGES = "../assets/img";

// ga and mt missing, nb for no
const languageList = [
  "bg",
  "cs",
  "da",
  "de",
  "el",
  "es",
  "et",
  "fr",
  "hr",
  "hu",
  "it",
  "lt",
  "lv",
  "nl",
  "nb",
  "pl",
  "pt",
  "ro",
  "sk",
  "sl",
  "fi",
  "sv",
];
languageList.forEach((lang) => {
  const locale = localeModules[`../config/langs/${lang}.json`];
  if (locale) {
    registerLocale(locale.default);
  }
});

/**
 * @description         Returns an array that contains unique values
 *                      of the given properties in the given array.
 * @param { String }    prop   - The key of {array} which values will be unique.
 * @param { [Object] }  array - The array to take keys from.
 * @returns { [*] }     A new Array containing unique values of the {arrays} {key} property
 */
function unique(prop, array) {
  // Filter elements in {array} that do not have a {prop} key.
  return [
    ...new Set(
      array
        .filter(
          // Remove duplicates by creating a Set and remove items where {prop} has no value.
          (item) =>
            Object.prototype.hasOwnProperty.call(item, prop) && !!item[prop]
        )
        // Create a new array containing the {prop} values of each given item.
        .map((item) => item[prop])
    ),
  ];
}

/**
 * @description         Abstract function that returns an image from /assets/img
 * @param { String }    image - The path to the image from /assets/img without fileending e.g. "/flags/eu"
 * @param { String }    defaultFallbackImage - The path to the default fallback image from /assets/img without fileending e.g. "/flags/eu"
 * @returns { String }  An image, represented by its absolute path.
 */
function getImg(image = "", defaultFallbackImage = "") {
  const maybeImageFrontSlash = image.startsWith("/") ? image : `/${image}`;
  const maybeFallbackImageFrontSlash = defaultFallbackImage.startsWith("/")
    ? defaultFallbackImage
    : `/${defaultFallbackImage}`;
  let img;

  try {
    img = imageModules[`${RELATIVE_PATH_TO_IMAGES}${maybeImageFrontSlash}.png`];
    if (!img) {
      throw new Error("Image not found");
    }
  } catch (err) {
    if (defaultFallbackImage)
      img =
        imageModules[
          `${RELATIVE_PATH_TO_IMAGES}${maybeFallbackImageFrontSlash}.png`
        ];
    else img = imageModules[`${RELATIVE_PATH_TO_IMAGES}/img-not-available.png`];
  }

  return img.default;
}

/**
 * @description         Returns an image of a flag.
 * @param { String }    countryId - The ID (example: 'en', 'de', 'fr') of a country to get the flag from.
 * @returns { String }  An image, represented by its absolute path.
 */
function getCountryFlagImg(countryId) {
  let img;
  try {
    img =
      imageModules[
        `${RELATIVE_PATH_TO_IMAGES}/flags/${countryId.toLowerCase()}.png`
      ];
  } catch (err) {
    img = imageModules[`${RELATIVE_PATH_TO_IMAGES}/flags/eu.png`];
  }
  return img.default;
}

function getRepresentativeLocaleOf(prop, userLocale, fallbacks) {
  if (!prop || isNil(prop) || (!isObject(prop) && !isString(prop)))
    return undefined;
  // Check if prop is only a string without translations
  if (isString(prop)) return prop;
  // Use language setting of user
  if (has(prop, userLocale)) return userLocale;
  // Iterate over given fallback languages
  if (fallbacks && isArray(fallbacks)) {
    const foundLang = fallbacks.find(
      (lang) => lang && isString(lang) && has(prop, lang.toLowerCase())
    );
    if (foundLang) return foundLang;
  }
  // Use the first language in the given property if none of the languages is present
  const key = Object.keys(prop)[0];
  if (key) return key;
  // Use default text if prop does not have any items
  return undefined;
}

/**
 * @description         Checks if a translation for the given prop parameter is available and returns it in the following priority order:
 *                      1. User set locale
 *                      2. Given fallback languages
 *                      3. Any available language
 * @param { Object }    prop - The object that should contain the translations
 * @param { String }    userLocale - The currently set locale.
 * @param { [String] }  fallbacks - The fallback languages to check for, when given locale is not available in given prop
 * @returns { String }  A translated text.
 */
function getTranslationFor(prop, userLocale, fallbacks) {
  const locale = getRepresentativeLocaleOf(prop, userLocale, fallbacks);
  return locale ? prop[locale] : undefined;
}

/**
 * @description Returns the translation for a facet item
 * @param  { String } fieldId
 * @param { String } facetId
 * @param { String } userLocale
 * @param { String } fallback
 * @returns { String } The translated facet item, if available
 */
function getFacetTranslation(fieldId, facetId, userLocale, fallback) {
  const normalizeProvenanceLabel = (label) => {
    if (!isString(label)) return label;
    return label.trim().toLowerCase() === "eu institutions" ? "Europe" : label;
  };

  if (fieldId === "country") {
    const locale = userLocale === "no" ? "nb" : userLocale;
    const name = getName(facetId, locale);
    // Translation missing because this.$t('message.catalogFacets.euInstitutions') is not working here
    // return isNil(name)
    //   ? (fallback === 'EU institutions' ? this.$t('message.catalogFacets.euInstitutions') : fallback)
    //   : name;
    return normalizeProvenanceLabel(isNil(name) ? fallback : name);
  }
  if (isObject(fallback)) {
    return has(fallback, userLocale)
      ? fallback[userLocale]
      : has(fallback, "en")
      ? fallback.en
      : Object.keys(fallback).length > 0
      ? fallback[0]
      : "No title available";
  }
  return isNil(fallback)
    ? "No title available"
    : isString(fallback)
    ? fallback
    : fallback.toString();
}

/**
 * Truncates a String to a maximum character count of maxChars
 * @param text
 * @param maxChars
 * @param noAppend
 */
function truncate(text, maxChars, noAppend) {
  if (!text) return "";
  const trunc = text.substring(0, maxChars);
  if (noAppend || text.length <= maxChars) return trunc;
  return `${trunc}...`;
}

/**
 * normalizing the dataset id
 * @param str string to be normalized
 */
function normalize(str) {
  const normalized = str.normalize("NFKD");
  return normalized
    .replace("%", "")
    .replace("\\W", "-")
    .replace("-+", "-")
    .toLowerCase();
}

/**
 * remove mailto or tel
 * @param str string
 */
function removeMailtoOrTel(str) {
  return str.replace(/^(mailto|tel):/, "");
}

function replaceHttp(str) {
  try {
    const url = new URL(str);
    if (url.protocol === "http:") {
      url.protocol = "https:";
    }
    return url.href;
  } catch (ex) {
    // Return original string if it is not a valid URL
    return str;
  }
}

/**
 * Returns a function that takes an object and returns a modified object
 * where for each dstProp in dstProps holds: object.dstProp === object.srcProp
 *
 * This function aims to help maintain stability against DCAT-AP schema changes
 * by providing alternative keys names for access
 *
 * @example
 * const foo = { foo: 'hello world' };
 * const mirrorFooAsBar = mirrorPropertyFn('foo', 'bar');
 * const mirrored = mirrorFooAsBar(foo);
 * log(mirrored.foo) // -> 'hello world'
 * log(mirrored.bar) // -> 'hello world'
 * @param {String} srcProp
 * @param {String | Array<String>} dstProps
 * @returns {Function}
 */
function mirrorPropertyFn(srcProp, dstProps) {
  const dstPropsArray = isArray(dstProps) ? dstProps : [dstProps];

  // Return function that returns a proxy that does the mirroring when
  // accessing dstProps
  return (obj) =>
    new Proxy(
      {
        // Add preliminary dstProps to object so lodash _.has won't return false
        ...obj,
        ...dstPropsArray.reduce((acc, prop) => {
          // eslint-disable-next-line no-param-reassign
          acc[prop] = obj[srcProp];
          return acc;
        }, {}),
      },
      {
        get(target, prop, receiver) {
          // If accessing dstProp, return srcProp value
          const foundTargetProp = dstPropsArray.includes(srcProp);
          const maybeRedirectedProp = foundTargetProp ? srcProp : prop;
          return Reflect.get(target, maybeRedirectedProp, receiver);
        },
      }
    );
}

/**
 * @description Function for determining of given data is of type object
 * @param {*} data
 * @returns Boolean determining, if data is object
 */
function matchesObjectStructure(data) {
  const dataKeys = Object.keys(data);
  const firstValue = data[dataKeys[0]];

  if (typeof firstValue === "string" || typeof firstValue === "number") {
    return true;
  }
  return false;
}

/**
 * @description Function to search for all properties in inputconfiguration which provide a 'source'-property.
 * Each name provided by a 'source'-property is saved inside the propertyNamesArray and returned as an array of property-names.
 * @param {Array} inputConfigArray Array of inputconfiguration containing information about components to render.
 * @param {Array} propertyNamesArray Array of names retrieved from components with a 'source'-property.
 * @returns {Array} Array with all names retireved (popertyNamesArray)
 */
function findPropertiesWithSources(inputConfigArray, propertyNamesArray) {
  for (const index in inputConfigArray) {
    // only subcomponents without a 'children'-property contain a 'source'-property
    // if there is a 'children'-property the current component isn't a subcomponent so
    // the function needs be called again on this component to get to the subcomponent
    if (Object.keys(inputConfigArray[index]).includes("children")) {
      findPropertiesWithSources(
        inputConfigArray[index].children,
        propertyNamesArray
      );
    } else if (inputConfigArray[index].type === "conditional-input") {
      const conditionalInputKeys = Object.keys(inputConfigArray[index].data);
      for (const inputKeyIndex in conditionalInputKeys) {
        findPropertiesWithSources(
          inputConfigArray[index].data[conditionalInputKeys[inputKeyIndex]],
          propertyNamesArray
        );
      }
    } else {
      // not all subcomponents contain a 'source'-property
      if (Object.keys(inputConfigArray[index]).includes("source")) {
        // the 'source'-property provides a name which links to further information in the generalconfig-file
        if (!propertyNamesArray.includes(inputConfigArray[index].source.name)) {
          propertyNamesArray.push(inputConfigArray[index].source.name);
        }
      }
    }
  }

  return propertyNamesArray;
}

/**
 *
 * @param {*} dataset
 * @param {*} properties
 * @param {*} translatableProperties
 */
function setTranslation(
  dataset,
  properties,
  translatableProperties,
  languageInformation
) {
  for (const propertyIndex in properties) {
    const propertyName = properties[propertyIndex];
    const propertyValue = dataset[propertyName];

    if (translatableProperties.includes(propertyName)) {
      const translationSelectoren = Object.keys(
        languageInformation.translation[languageInformation.locale].message
          .dataupload[propertyName]
      );

      if (propertyValue !== "") {
        if (translationSelectoren.includes(propertyValue)) {
          dataset[propertyName] =
            languageInformation.translation[
              languageInformation.locale
            ].message.dataupload[propertyName][propertyValue];
        }
      }
    }
  }
}

/**
 * @description Appends current locale to an url
 * @param {String} url url
 */
function appendCurrentLocaleToURL(url) {
  try {
    const urlHost = new URL(url).host;
    const baseUrlHost = new URL(this.$env.api.baseUrl).host;
    const isOurHostname = urlHost === baseUrlHost;
    if (isOurHostname) {
      return `${url}?locale=${this.$route.query.locale}`;
    }
    return url;
  } catch {
    // when there is no hostname then it should link to our website
    return `${url}?locale=${this.$route.query.locale}`;
  }
}

/**
 * @description Add preceding zero for numbers < 100 (if not already existing)
 * @param {String} value value
 */
function addPrecedingZero(value) {
  return `${parseInt(value, 10) < 10 ? 0 : ""}${parseInt(value, 10)}`;
}

/**
 * @description Formatting temporalResolution property into human-readable format
 * @param {String} datetime datetime in temporalResolution format
 */
function formatDatetime(datetime) {
  let date = datetime.split("T")[0].substr(1);

  const year = date.split("Y")[0];
  date = date.split("Y")[1];

  const month = addPrecedingZero(date.split("M")[0]);
  date = date.split("M")[1];

  const day = addPrecedingZero(date.split("D")[0]);

  let time = datetime.split("T")[1];

  const hour = addPrecedingZero(time.split("H")[0]);
  time = time.split("H")[1];

  const minute = addPrecedingZero(time.split("M")[0]);
  time = time.split("M")[1];

  const second = addPrecedingZero(time.split("S")[0]);

  return `${hour}:${minute}:${second} - ${day}.${month}.${year}`;
}

function alternateImage() {
  let a =
    "data:image/png;base64,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";

  return a;
}

/**
 * @description Creates a navigation guard that adds a locale to the query if it is not present.
 */
function createStickyLocale(fallbackLocale: string = "en") {
  return function stickyLocale(to: any, from: any, next: Function) {
    const toLocale = to.query.locale;
    const fromLocale = from.query.locale;

    const newLocale = toLocale ?? fromLocale ?? fallbackLocale;

    newLocale !== toLocale
      ? next({ ...to, query: { ...to.query, locale: newLocale } })
      : next();
  };
}

// Export all functions as default export.
export {
  unique,
  getImg,
  getCountryFlagImg,
  getFacetTranslation,
  getRepresentativeLocaleOf,
  getTranslationFor,
  truncate,
  normalize,
  removeMailtoOrTel,
  replaceHttp,
  mirrorPropertyFn,
  matchesObjectStructure,
  findPropertiesWithSources,
  setTranslation,
  appendCurrentLocaleToURL,
  addPrecedingZero,
  formatDatetime,
  createStickyLocale,
  alternateImage,
};
